import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuButton, MenuItems } from '@headlessui/react';
import {
  QuestionMarkCircleIcon,
  XMarkIcon,
  CursorArrowRaysIcon,
  MagnifyingGlassPlusIcon,
  CircleStackIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

type HelpItem = {
  icon: React.ComponentType<{ className?: string }>;
  titleKey: string;
  title: string;
  descKey: string;
  desc: string;
  color: string;
  shortcut?: string;
};

export const NetworkHelpMenu = () => {
  const { t } = useTranslation();

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let lastTriggerTime = 0;
    const DEBOUNCE_MS = 100; // 100ms以内の重複イベントを無視

    const onKey = (e: KeyboardEvent) => {
      // アクティブな要素が入力系の場合は無視
      const activeElement = document.activeElement;
      if (
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.getAttribute('contenteditable') === 'true'
      ) {
        return;
      }

      // MenuButtonのaria-expanded属性をチェックしてメニューが開いているか判定
      const isMenuOpen =
        triggerRef.current?.getAttribute('aria-expanded') === 'true';

      if (isMenuOpen) {
        return;
      }

      const isQuestion = e.key === '?' || (e.shiftKey && e.key === '/');
      if (isQuestion) {
        // デバウンス: 短時間に複数回トリガーされるのを防ぐ
        const now = Date.now();
        if (now - lastTriggerTime < DEBOUNCE_MS) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        lastTriggerTime = now;

        e.preventDefault();
        e.stopPropagation();

        // MenuButtonが存在する場合のみクリック
        if (triggerRef.current) {
          // フォーカスを当ててからEnterキーを発火
          triggerRef.current.focus();

          // キーボードイベント(Space)をシミュレート
          const keydownEvent = new KeyboardEvent('keydown', {
            key: ' ',
            code: 'Space',
            keyCode: 32,
            which: 32,
            bubbles: true,
            cancelable: true,
          });

          const keyupEvent = new KeyboardEvent('keyup', {
            key: ' ',
            code: 'Space',
            keyCode: 32,
            which: 32,
            bubbles: true,
            cancelable: true,
          });

          triggerRef.current.dispatchEvent(keydownEvent);
          triggerRef.current.dispatchEvent(keyupEvent);
        }
      }
    };

    // captureフェーズでイベントを捕捉して優先度を上げる
    window.addEventListener('keydown', onKey, { capture: true });

    return () => {
      window.removeEventListener('keydown', onKey, { capture: true });
    };
  }, []);

  const helpItems: HelpItem[] = [
    {
      icon: CursorArrowRaysIcon,
      titleKey: 'networkHelp.pan.title',
      title: t('networkHelp.pan.title'),
      descKey: 'networkHelp.pan.desc',
      desc: t('networkHelp.pan.desc'),
      color: 'text-blue-600',
      shortcut: '↑↓ / Shift+↔',
    },
    {
      icon: MagnifyingGlassPlusIcon,
      titleKey: 'networkHelp.zoom.title',
      title: t('networkHelp.zoom.title'),
      descKey: 'networkHelp.zoom.desc',
      desc: t('networkHelp.zoom.desc'),
      color: 'text-green-600',
      shortcut: 'Ctrl+Wheel',
    },
    {
      icon: CircleStackIcon,
      titleKey: 'networkHelp.node.title',
      title: t('networkHelp.node.title'),
      descKey: 'networkHelp.node.desc',
      desc: t('networkHelp.node.desc'),
      color: 'text-purple-600',
    },
    {
      icon: FunnelIcon,
      titleKey: 'networkHelp.filter.title',
      title: t('networkHelp.filter.title'),
      descKey: 'networkHelp.filter.desc',
      desc: t('networkHelp.filter.desc'),
      color: 'text-orange-600',
    },
  ];

  return (
    <Menu>
      {({ close }) => {
        // メニュー内でのキーイベントハンドラ
        const handleMenuKeyDown = (e: React.KeyboardEvent) => {
          const isQuestion = e.key === '?' || (e.shiftKey && e.key === '/');
          if (isQuestion) {
            e.preventDefault();
            e.stopPropagation();
            close();
          }
        };

        return (
          <>
            <MenuButton
              ref={triggerRef}
              aria-label={t('networkHelp.openLabel')}
              className="group flex size-9 items-center justify-center rounded-full bg-white/10 transition-all hover:bg-white/20 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-white/30"
            >
              <QuestionMarkCircleIcon className="size-5 text-white transition-transform group-hover:scale-110" />
            </MenuButton>
            <MenuItems
              transition
              anchor="bottom end"
              onKeyDown={handleMenuKeyDown}
              className="data-closed:scale-95 data-closed:opacity-0 w-96 origin-top-right rounded bg-white p-1 text-sm/6 shadow-xl transition duration-100 ease-out focus:outline-none"
            >
              <div
                ref={contentRef}
                tabIndex={-1}
                role="region"
                aria-labelledby="network-help-title"
                className="p-4"
              >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                  <div className="flex items-center gap-2">
                    <QuestionMarkCircleIcon className="size-5 text-blue-600" />
                    <h3
                      id="network-help-title"
                      className="text-base font-bold text-gray-900"
                    >
                      {t('networkHelp.title')}
                    </h3>
                  </div>
                  <button
                    type="button"
                    aria-label={t('networkHelp.close')}
                    className="inline-flex size-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    onClick={() => close()}
                  >
                    <XMarkIcon className="size-5" />
                  </button>
                </div>

                {/* Help Items */}
                <div className="mt-4 space-y-3">
                  {helpItems.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={index}
                        className="group rounded-lg border border-gray-100 bg-gray-50/50 p-3 transition-all hover:border-gray-200 hover:bg-white hover:shadow-sm"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-0.5 shrink-0 ${item.color}`}>
                            <Icon className="size-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <h4 className="text-sm font-semibold text-gray-900">
                                {item.title}
                              </h4>
                              {item.shortcut && (
                                <kbd className="rounded border border-gray-200 bg-gray-100 px-2 py-0.5 font-mono text-[10px] text-gray-600">
                                  {item.shortcut}
                                </kbd>
                              )}
                            </div>
                            <p className="mt-1 text-xs leading-relaxed text-gray-600">
                              {item.desc}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-3">
                  <div className="text-[10px] text-gray-400">
                    {t('networkHelp.pressKeyToToggle', 'Press')}{' '}
                    <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono">
                      ?
                    </kbd>{' '}
                    {t('networkHelp.toToggle', 'to toggle')}
                  </div>
                </div>
              </div>
            </MenuItems>
          </>
        );
      }}
    </Menu>
  );
};
